import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowUpRight, ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";

const NEW_TITLES = [
  ["Workshop build", "On the bench"],
  ["Frame in progress", "Build day"],
  ["Tread by tread", "Hand-fit"],
  ["Open riser detail", "Floating"],
  ["Carriage & stringer", "Joinery"],
  ["Mid-flight view", "Install"],
  ["Site measure", "Pre-build"],
  ["Final finish", "Project complete"],
];

const NEW_FILES = [
  "s20", "s21", "s22", "s23", "s24", "s25", "s26", "s27", "s28", "s29",
  "s30", "s31", "s32", "s33", "s34", "s35", "s36", "s37", "s38", "s39",
  "s40", "s41", "s42", "s43", "s44", "s45", "s46", "s47", "s48", "s49",
];

const BASE_SLIDES = [
  { src: "/showcase/s02.jpg", title: "Open-tread oak build", tag: "Floating staircase" },
  { src: "/showcase/s03.jpg", title: "Landing & balustrade", tag: "Install in progress" },
  { src: "/showcase/s04.jpg", title: "Sweep & rise", tag: "Custom build" },
  { src: "/showcase/s05.jpg", title: "Above the treads", tag: "Detail · install" },
  { src: "/showcase/s06.jpg", title: "Mid-build geometry", tag: "On-site" },
  { src: "/showcase/s07.jpg", title: "Frame & treads", tag: "Build day" },
  { src: "/showcase/s08.jpg", title: "Open riser, oak", tag: "Floating · finish" },
  { src: "/showcase/s09.jpg", title: "Wall-anchored treads", tag: "Floating" },
  { src: "/showcase/s10.jpg", title: "Switchback finish", tag: "Replacement · install" },
  { src: "/showcase/s11.jpg", title: "Before · curved oak", tag: "Replacement · removal day" },
  { src: "/showcase/s12.jpg", title: "Spiral, from above", tag: "Before · demolition" },
  { src: "/showcase/s13.jpg", title: "Floating frame", tag: "Replacement · rebuild" },
  { src: "/showcase/s14.jpg", title: "Mid-rise treads", tag: "Floating · install" },
];

const SLIDES = [
  ...BASE_SLIDES,
  ...NEW_FILES.map((name, i) => {
    const [title, tag] = NEW_TITLES[i % NEW_TITLES.length];
    return { src: `/showcase/${name}.jpg`, title, tag };
  }),
];

const AUTO_MS = 5000;

export const Showcase = () => {
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const timer = useRef(null);

  const goTo = useCallback((i) => {
    setIndex(((i % SLIDES.length) + SLIDES.length) % SLIDES.length);
  }, []);

  const next = useCallback(() => goTo(index + 1), [index, goTo]);
  const prev = useCallback(() => goTo(index - 1), [index, goTo]);

  useEffect(() => {
    if (!playing) return undefined;
    timer.current = setTimeout(() => setIndex((i) => (i + 1) % SLIDES.length), AUTO_MS);
    return () => clearTimeout(timer.current);
  }, [index, playing]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-4" data-testid="showcase-grid">
      {/* Main stage */}
      <div className="md:col-span-9 relative border hairline bg-card aspect-[4/3] md:aspect-[16/10] overflow-hidden group">
        {SLIDES.map((s, i) => (
          <img
            key={s.src}
            src={s.src}
            alt={s.title}
            loading={i === 0 ? "eager" : "lazy"}
            style={{ filter: "brightness(1.05) contrast(1.03) saturate(1.03)" }}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
              i === index ? "opacity-100" : "opacity-0"
            }`}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-t from-background/85 via-background/10 to-transparent pointer-events-none" />

        {/* Caption */}
        <div className="absolute inset-x-0 bottom-0 p-6 lg:p-8 flex items-end justify-between gap-6">
          <div>
            <p className="text-sm uppercase tracking-[0.28em] text-primary">
              Build · {String(index + 1).padStart(2, "0")} / {String(SLIDES.length).padStart(2, "0")}
            </p>
            <p className="font-display text-3xl md:text-4xl text-foreground mt-2 leading-tight">
              {SLIDES[index].title}
            </p>
            <p className="text-sm uppercase tracking-[0.24em] text-muted-foreground mt-2">
              {SLIDES[index].tag}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setPlaying((p) => !p)}
            data-testid="showcase-play-toggle"
            aria-label={playing ? "Pause slideshow" : "Play slideshow"}
            className="border border-primary/50 text-primary px-3 py-2 hover:bg-primary/10 transition-colors flex items-center gap-2 text-sm uppercase tracking-[0.22em]"
          >
            {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
            {playing ? "Pause" : "Play"}
          </button>
        </div>

        {/* Arrows */}
        <button
          type="button"
          onClick={prev}
          data-testid="showcase-prev"
          aria-label="Previous"
          className="absolute left-3 top-1/2 -translate-y-1/2 h-12 w-12 border border-primary/40 text-primary bg-background/30 backdrop-blur-sm hover:bg-primary/15 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={next}
          data-testid="showcase-next"
          aria-label="Next"
          className="absolute right-3 top-1/2 -translate-y-1/2 h-12 w-12 border border-primary/40 text-primary bg-background/30 backdrop-blur-sm hover:bg-primary/15 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100"
        >
          <ChevronRight className="h-5 w-5" />
        </button>

        {/* Progress bar */}
        <div className="absolute inset-x-0 top-0 h-[2px] bg-primary/10">
          <div
            key={`${index}-${playing}`}
            className="h-full bg-primary"
            style={{
              width: "100%",
              transformOrigin: "left",
              animation: playing ? `slide-progress ${AUTO_MS}ms linear forwards` : "none",
            }}
          />
        </div>
      </div>

      {/* Thumbnails */}
      <div className="md:col-span-3 grid grid-cols-5 md:grid-cols-2 gap-2 md:max-h-[60vh] md:overflow-y-auto pr-1">
        {SLIDES.map((s, i) => (
          <button
            type="button"
            key={s.src}
            onClick={() => goTo(i)}
            data-testid={`showcase-thumb-${i + 1}`}
            aria-label={`View slide ${i + 1}`}
            className={`relative aspect-square overflow-hidden border transition-all duration-300 ${
              i === index
                ? "border-primary shadow-[0_0_18px_rgba(212,175,55,0.35)]"
                : "hairline hover:border-primary/50 opacity-70 hover:opacity-100"
            }`}
          >
            <img src={s.src} alt="" loading="lazy" className="w-full h-full object-cover" />
          </button>
        ))}
      </div>

      <style>{`
        @keyframes slide-progress {
          from { transform: scaleX(0); }
          to   { transform: scaleX(1); }
        }
      `}</style>
    </div>
  );
};
